import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  CircleQuestionmarkIcon,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@column/ui';

import {
  OptionsFullGranularWithDefault,
  OptionsOnOffWithDefault,
  OptionsThisBankAccount,
  OptionsThisEntity,
  Permissions,
} from '../types';
import { RenderFields } from '~/components';
import { type SectionField } from '~/components/RenderFields';

import { PermissionSelect } from './PermissionSelect';
import { PermissionToggles } from './PermissionToggles';

const toOnOff = (value?: string) =>
  value === 'default' ? 'default' : value === 'write' || value === 'read' ? 'on' : 'off';
const fromOnOff = (value: string) => (value === 'on' ? 'write' : value === 'off' ? 'none' : 'default');

interface CustomOverridesModalProps {
  breadcrumbs: string[];
  open: boolean;
  onClose: () => void;
  onSave: (permissions: Permissions) => void;
  currentPermissions: Permissions;
  parentPermissions: Permissions;
  permissionsToLabelsMap: { [permission: string]: string };
}
export const CustomOverridesModal: React.FC<CustomOverridesModalProps> = ({
  breadcrumbs,
  currentPermissions,
  open,
  onClose,
  onSave,
  parentPermissions,
  permissionsToLabelsMap,
}) => {
  const showTransferApprovals = 'approveTransfers' in permissionsToLabelsMap;
  const permissionsTogglesToLabelsMap = useMemo(() => {
    const p = { ...permissionsToLabelsMap };
    delete p.approveTransfers;
    delete p.bankAccounts;
    delete p.thisEntity;
    delete p.thisBankAccount;
    return p;
  }, [permissionsToLabelsMap]);

  const parentName = useMemo(() => {
    if (breadcrumbs.length < 2) {
      return 'the platform';
    }
    return breadcrumbs[1];
  }, [breadcrumbs]);

  const form = useForm<Permissions>({
    values: currentPermissions,
  });

  const { control, getValues, setValue, reset } = form;
  const values = useWatch({ control });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const hasOverrides = useMemo(() => Object.values(values).some((value) => value !== 'default'), [values]);

  const resetOverrides = useCallback(() => {
    Object.keys(permissionsToLabelsMap).forEach((permission) => {
      setValue(permission, 'default');
    });
  }, [permissionsToLabelsMap, setValue]);

  const overrideField = (
    id: string,
    label: string,
    description: string,
    options: { label: string; value: string }[]
  ): SectionField<Permissions, undefined> => ({
    id,
    hide: !currentPermissions[id],
    label,
    labelVariant: values[id] !== 'default' ? 'primary' : undefined,
    description,
    children: ({ value, onChange }) => (
      <PermissionSelect aria-label={label} value={value ?? 'default'} options={options} onChange={onChange} />
    ),
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-3 sm:max-w-3xl"
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the dialog instead of ringing it.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        <DialogHeader className="-mx-6 -mt-2 flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <div className="grid min-w-0 gap-1">
            <DialogTitle>Custom Overrides</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-0.5">
              {breadcrumbs.map((breadcrumb, index) => (
                <Fragment key={`${index}-${breadcrumb}`}>
                  {index > 0 && <Icon.ChevronRight className="[--icon-size-x:14px] [--icon-size-y:14px]" />}
                  <span className="truncate">{breadcrumb}</span>
                </Fragment>
              ))}
            </DialogDescription>
          </div>
          <DialogClose asChild>
            {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
            <button
              type="button"
              aria-label="Close"
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>
        <FormProvider {...form}>
          <div className="-mx-6 -mt-3 [--page-margin:24px]">
            {RenderFields<Permissions>({
              sections: [
                {
                  headline: 'Transfers',
                  notifications: hasOverrides && (
                    <div className="flex items-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex text-gray-350 transition-colors hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400">
                              <CircleQuestionmarkIcon />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            By default, permissions are inherited from {parentName}. Permissions highlighted in blue
                            override permissions from {parentName}.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        type="button"
                        size="xs"
                        variant="subtle-primary"
                        className="px-0"
                        onClick={resetOverrides}
                      >
                        Clear overrides
                        <Icon.Reset className="[--icon-size-x:14px] [--icon-size-y:14px]" />
                      </Button>
                    </div>
                  ),
                  content: (
                    <PermissionToggles
                      control={control}
                      parentPermissions={parentPermissions}
                      permissionsToLabelsMap={permissionsTogglesToLabelsMap}
                    />
                  ),
                },
                {
                  headline: 'Controls',
                  hide: !showTransferApprovals,
                  fields: [
                    {
                      id: 'approveTransfers',
                      label: 'Approve Transfers',
                      labelVariant: values.approveTransfers !== 'default' ? 'primary' : undefined,
                      description: 'Set the permissions for approving or rejecting transfers in review.',
                      children: ({ value, onChange }) => (
                        <PermissionSelect
                          aria-label="Approve Transfers"
                          value={toOnOff(value)}
                          options={OptionsOnOffWithDefault}
                          onChange={(newValue) => onChange(fromOnOff(newValue))}
                        />
                      ),
                    },
                  ],
                },
                {
                  headline: 'Edit Access',
                  fields: [
                    overrideField(
                      'thisBankAccount',
                      'This Bank Account',
                      'Set edit permissions on this Account.',
                      OptionsThisBankAccount
                    ),
                    overrideField(
                      'thisEntity',
                      'This Entity',
                      'Set edit permissions on this Entity.',
                      OptionsThisEntity
                    ),
                    overrideField(
                      'bankAccounts',
                      'Bank Accounts on Entity',
                      'Set edit permissions on the underlying Accounts on this Entity.',
                      OptionsFullGranularWithDefault
                    ),
                  ],
                },
              ],
            })}
          </div>
        </FormProvider>
        <DialogFooter className="-mt-3 sm:justify-between">
          <Button type="button" size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => onSave(getValues())}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
